import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

/**
 * Sessão isolada por aba — "Ver portal como paciente" (16/set).
 *
 * O admin abre o portal de um paciente numa aba nova SEM perder a sessão dele.
 * O GoTrue guarda a sessão em localStorage (uma só pra todas as abas da origem)
 * e ainda sincroniza SIGNED_IN/SIGNED_OUT entre abas por BroadcastChannel com o
 * nome do storageKey. Nesta aba, portanto: sessão em sessionStorage (morre com
 * a aba) E storageKey próprio (canal de broadcast próprio) — sem isso a sessão
 * do paciente pisaria na do admin na aba ao lado.
 *
 * A marca nasce na primeira carga de /ver-como (o client é criado antes de
 * qualquer componente rodar) e fica no sessionStorage da própria aba: sobrevive
 * a navegação e F5, não sobrevive a fechar a aba. Aba nova aberta a partir desta
 * (window.open) herda uma CÓPIA do sessionStorage — comportamento do navegador —
 * então também nasce isolada, o que é o esperado.
 */
export const SESSAO_ISOLADA_KEY = 'essence:sessao-isolada';

/** Esta janela é um iframe (pop-up "ver aqui" da ficha)? Iframe da mesma origem
 *  COMPARTILHA o sessionStorage com a página de cima — a sessão do paciente e
 *  qualquer marca gravada lá vazariam pra aba do admin. Embutido, a sessão fica
 *  só em memória: fechou o pop-up, morreu. */
export function isEmbutido(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

export function isSessaoIsolada(): boolean {
  if (isEmbutido()) return window.location.pathname === '/ver-como' || memoriaTemSessao;
  try {
    if (sessionStorage.getItem(SESSAO_ISOLADA_KEY) === '1') return true;
    if (window.location.pathname === '/ver-como') {
      sessionStorage.setItem(SESSAO_ISOLADA_KEY, '1');
      return true;
    }
  } catch {
    // sessionStorage bloqueado (modo privado restrito): segue como aba comum
  }
  return false;
}

// Storage em memória pro modo embutido. Depois que o iframe navega de /ver-como
// pro portal (SPA, sem recarregar), o pathname muda — a marca passa a ser "há
// sessão em memória", por isso o flag abaixo.
let memoriaTemSessao = false;
const memoria = new Map<string, string>();
const storageEmMemoria = {
  getItem: (k: string) => memoria.get(k) ?? null,
  setItem: (k: string, v: string) => { memoria.set(k, v); memoriaTemSessao = true; },
  removeItem: (k: string) => { memoria.delete(k); },
};

/**
 * Cada sessão isolada tem a SUA chave de storage — e, com ela, o seu canal de
 * broadcast. O supabase-js cria um BroadcastChannel com o nome da storageKey e
 * avisa SIGNED_IN/SIGNED_OUT por ele; enquanto a aba isolada e o pop-up da
 * ficha usavam a mesma chave fixa, sair de uma derrubava a outra (achado da
 * auditoria de 24/set: o pop-up caía no login quando a aba voltava a ser admin).
 * Duas abas abertas em pacientes diferentes tinham o mesmo problema.
 *
 * Pop-up (embutido): chave sorteada a cada carga — a sessão vive só em memória
 * e morre junto. Aba: a chave nasce uma vez e fica guardada no sessionStorage
 * DA ABA, para sobreviver a F5 sem nunca alcançar a aba ao lado.
 */
export const CHAVE_DA_SESSAO_ISOLADA = 'essence:sessao-isolada-chave';

function sorteio(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
}

let chaveEmbutida: string | null = null;

/** Nome da storageKey desta sessão isolada (null quando a aba é comum). */
export function chaveDaSessaoIsolada(): string | null {
  if (!isSessaoIsolada()) return null;
  if (isEmbutido()) {
    if (!chaveEmbutida) chaveEmbutida = `sb-ver-como-popup-${sorteio()}`;
    return chaveEmbutida;
  }
  try {
    const guardada = sessionStorage.getItem(CHAVE_DA_SESSAO_ISOLADA);
    if (guardada) return guardada;
    const nova = `sb-ver-como-${sorteio()}`;
    sessionStorage.setItem(CHAVE_DA_SESSAO_ISOLADA, nova);
    return nova;
  } catch {
    return `sb-ver-como-${sorteio()}`;
  }
}

/** A chave desta janela, sem criar uma nova quando não existe. */
function chaveGuardada(): string | null {
  if (isEmbutido()) return chaveEmbutida;
  try { return sessionStorage.getItem(CHAVE_DA_SESSAO_ISOLADA); } catch { return null; }
}

function tokenDaSessaoIsolada(): string | null {
  const chave = chaveGuardada();
  if (!chave) return null;
  try {
    const bruto = isEmbutido() ? memoria.get(chave) ?? null : sessionStorage.getItem(chave);
    if (!bruto) return null;
    const guardado = JSON.parse(bruto);
    return guardado?.access_token ?? guardado?.currentSession?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Revoga no servidor SÓ esta sessão (`scope=local`), sem passar pelo client.
 *
 * `supabase.auth.signOut()` faria a mesma revogação, mas também dispara
 * SIGNED_OUT no BroadcastChannel — e era isso que derrubava o pop-up da ficha
 * aberto ao lado. Chamando o REST na mão, a sessão morre no GoTrue e nenhuma
 * outra janela fica sabendo.
 *
 * Não se espera a resposta: quem chama recarrega a página em seguida, e
 * `keepalive` mantém o pedido de pé depois disso. Falhou (sem rede, token já
 * morto)? A limpeza local segue do mesmo jeito.
 */
function revogarNoServidor(token: string | null): void {
  if (!token) return;
  try {
    void fetch(`${supabaseUrl}/auth/v1/logout?scope=local`, {
      method: 'POST',
      keepalive: true,
      headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${token}` },
    }).catch(() => { /* a aba já vai recarregar */ });
  } catch { /* sem fetch: segue a limpeza */ }
}

/** Encerra a sessão isolada DESTA janela: revoga no servidor e apaga as marcas.
 *  Sem signOut do client, que avisaria as outras janelas pelo canal. */
export function soltarSessaoIsolada(): void {
  revogarNoServidor(tokenDaSessaoIsolada());

  // Embutido, o sessionStorage é o DA PÁGINA DO ADMIN: apagar marcas aqui
  // mexeria na janela de quem está trabalhando. A sessão do pop-up mora na
  // memória deste iframe — limpar ela basta.
  if (isEmbutido()) {
    const chave = chaveGuardada();
    if (chave) memoria.delete(chave);
    return;
  }

  try {
    const chave = chaveGuardada();
    if (chave) {
      sessionStorage.removeItem(chave);
      memoria.delete(chave);
    }
    sessionStorage.removeItem(CHAVE_DA_SESSAO_ISOLADA);
    sessionStorage.removeItem(SESSAO_ISOLADA_KEY);
    sessionStorage.removeItem('patientPortalSession');
    // resto da época em que a chave era fixa
    sessionStorage.removeItem('sb-ver-como-auth-token');
  } catch { /* sem storage */ }
}

function authStorageIsolado() {
  const chave = chaveDaSessaoIsolada();
  if (!chave) return {};
  return {
    storage: isEmbutido() ? storageEmMemoria : window.sessionStorage,
    storageKey: chave,
  };
}

// Cliente sem tipagem forte para permitir flexibilidade
// Os tipos serão validados em runtime
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    ...authStorageIsolado(),
  },
});

/**
 * Chama uma função RPC do Supabase
 */
export async function callRPC<T = any>(
  functionName: string,
  params: Record<string, any> = {}
): Promise<T> {
  const { data, error } = await supabase.rpc(functionName, params);

  if (error) {
    console.error(`RPC Error [${functionName}]:`, error);
    throw error;
  }

  return data as T;
}

/**
 * Retry wrapper para chamadas de rede
 */
export async function retryRequest<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 2);
  }
}

/**
 * Fetch with timeout — prevents hanging requests on slow networks.
 * Uses XMLHttpRequest fallback to avoid browser extension interference (e.g. SimilarWeb).
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs = 15000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error: any) {
    // Return a synthetic failed response instead of throwing.
    // This prevents "Uncaught runtime errors" from browser extensions (e.g. SimilarWeb)
    // that intercept window.fetch and re-throw network errors.
    return new Response(null, { status: 0, statusText: 'Network Error' });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get current Supabase session access token (for authenticated API calls)
 */
export async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

export { supabaseUrl, supabaseAnonKey };
